import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useLingui } from "@lingui/react/macro"
import { useAnchoredPosition } from "@/hooks/use-anchored-position"

// Shared inline-edit date popover (CON-206). Extracted from
// the deal Properties card so the inbox NEXT STEPS rail
// reuses the same month-grid calendar. Portals to body so an
// overflow-clip ancestor (a slideover / rail scroll
// container) can't clip it.
//
// The popover works in DATE-ONLY ISO strings ("2026-05-12")
// — `selectedIso` prefills the grid, and `onApply` hands back
// a date-only ISO (or `null` from a "Clear"-style preset).
// Callers that need a full timestamp (the rail's `due_at`)
// fold the picked date onto a time-of-day themselves.
//
// `presets` is caller-supplied so each surface keeps its own
// quick-pick row (the deal card includes its AI-suggestion
// presets + "Clear"; the rail uses Today / Tomorrow /
// In 1 week / End of month).

export const DATE_POPOVER_WIDTH = 280

export interface DatePopoverPreset {
  label: string
  // The date-only ISO this preset applies, or `null` to
  // clear the value (deal "Clear").
  iso: string | null
}

interface DatePopoverProps {
  anchor: HTMLElement
  selectedIso: string | null
  submitting: boolean
  error: string | null
  presets: DatePopoverPreset[]
  onClose: () => void
  onApply: (iso: string | null) => void
  // Accessible name for the dialog. Defaults to a generic
  // label; callers pass a surface-specific one.
  ariaLabel?: string
}

export function DatePopover({
  anchor,
  selectedIso,
  submitting,
  error,
  presets,
  onClose,
  onApply,
  ariaLabel,
}: DatePopoverProps) {
  const { t } = useLingui()
  const resolvedAriaLabel = ariaLabel ?? t`Pick a date`
  const selectedDate = parseIso(selectedIso)
  const [viewYear, setViewYear] = useState(
    (selectedDate ?? new Date()).getFullYear(),
  )
  const [viewMonth, setViewMonth] = useState(
    (selectedDate ?? new Date()).getMonth(),
  )

  const position = useAnchoredPosition(anchor, DATE_POPOVER_WIDTH)
  const popoverRef = useRef<HTMLDivElement>(null)

  // Close on Esc and on outside click.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation()
        onClose()
      }
    }
    function onPointer(e: MouseEvent) {
      const target = e.target as Node
      if (
        popoverRef.current &&
        !popoverRef.current.contains(target) &&
        !anchor.contains(target)
      ) {
        onClose()
      }
    }
    document.addEventListener("keydown", onKey, true)
    document.addEventListener("mousedown", onPointer)
    return () => {
      document.removeEventListener("keydown", onKey, true)
      document.removeEventListener("mousedown", onPointer)
    }
  }, [anchor, onClose])

  const today = new Date()
  const todayIso = isoFromDate(today)
  const monthNames = [
    t`January`,
    t`February`,
    t`March`,
    t`April`,
    t`May`,
    t`June`,
    t`July`,
    t`August`,
    t`September`,
    t`October`,
    t`November`,
    t`December`,
  ]

  function shiftMonth(delta: number) {
    let m = viewMonth + delta
    let y = viewYear
    if (m < 0) {
      m += 12
      y -= 1
    } else if (m > 11) {
      m -= 12
      y += 1
    }
    setViewMonth(m)
    setViewYear(y)
  }

  const firstOfMonth = new Date(viewYear, viewMonth, 1)
  const firstWeekday = firstOfMonth.getDay() // 0 = Sun
  const daysInMonth = new Date(
    viewYear,
    viewMonth + 1,
    0,
  ).getDate()

  // Render 6 rows × 7 days; pad with previous and next
  // month's overflow.
  const cells: { iso: string; day: number; muted: boolean }[] = []
  const prevMonth = new Date(viewYear, viewMonth - 1, 1)
  const daysInPrev = new Date(
    prevMonth.getFullYear(),
    prevMonth.getMonth() + 1,
    0,
  ).getDate()
  for (let i = firstWeekday - 1; i >= 0; i--) {
    const day = daysInPrev - i
    cells.push({
      iso: isoFromYMD(
        prevMonth.getFullYear(),
        prevMonth.getMonth(),
        day,
      ),
      day,
      muted: true,
    })
  }
  for (let day = 1; day <= daysInMonth; day++) {
    cells.push({
      iso: isoFromYMD(viewYear, viewMonth, day),
      day,
      muted: false,
    })
  }
  let nextDay = 1
  while (cells.length < 42) {
    const nextMonth = new Date(viewYear, viewMonth + 1, 1)
    cells.push({
      iso: isoFromYMD(
        nextMonth.getFullYear(),
        nextMonth.getMonth(),
        nextDay,
      ),
      day: nextDay,
      muted: true,
    })
    nextDay += 1
  }

  return createPortal(
    <div
      ref={popoverRef}
      role="dialog"
      aria-label={resolvedAriaLabel}
      style={{
        position: "fixed",
        top: position.top,
        left: position.left,
        zIndex: 60,
      }}
      className="w-[280px] rounded-xl bg-white border border-[#E5DFD3] shadow-[0px_24px_48px_-8px_#1E1B1726] p-3 flex flex-col gap-2"
    >
      <div className="flex items-center justify-between">
        <button
          type="button"
          aria-label={t`Previous month`}
          onClick={() => shiftMonth(-1)}
          className="w-7 h-7 rounded-md flex items-center justify-center hover:bg-[#F7F5F0]"
        >
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M10 4L6 8L10 12"
              stroke="#1E1B17"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <div className="font-medium text-foreground text-[13px] leading-4">
          {monthNames[viewMonth]} {viewYear}
        </div>
        <button
          type="button"
          aria-label={t`Next month`}
          onClick={() => shiftMonth(1)}
          className="w-7 h-7 rounded-md flex items-center justify-center hover:bg-[#F7F5F0]"
        >
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M6 4L10 8L6 12"
              stroke="#1E1B17"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
      <div className="grid grid-cols-7 text-center">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <div
            key={`dow-${i}`}
            className="text-[#7A746B] text-[10px] leading-3 py-1 uppercase tracking-wider"
          >
            {d}
          </div>
        ))}
        {cells.map((cell) => {
          const isSelected = cell.iso === selectedIso
          const isToday = cell.iso === todayIso
          return (
            <button
              key={cell.iso}
              type="button"
              onClick={() => onApply(cell.iso)}
              disabled={submitting}
              aria-pressed={isSelected}
              aria-label={cell.iso}
              className={[
                "h-8 w-8 mx-auto rounded-full text-[12px] leading-4 flex items-center justify-center cursor-pointer",
                isSelected
                  ? "bg-[#1E1B17] text-[#F0EDE6] font-semibold"
                  : cell.muted
                    ? "text-[#C9C2B6]"
                    : isToday
                      ? "text-[#B86A2D] font-semibold ring-1 ring-[#B86A2D]"
                      : "text-foreground hover:bg-[#F7F5F0]",
              ].join(" ")}
            >
              {cell.day}
            </button>
          )
        })}
      </div>
      <div className="flex flex-wrap gap-1.5 pt-1 border-t border-[#F0EDE6]">
        {presets.map((preset) => (
          <PresetChip
            key={preset.label}
            label={preset.label}
            onClick={() => onApply(preset.iso)}
            disabled={submitting}
          />
        ))}
      </div>
      {error ? (
        <p role="alert" className="text-[#B6675F] text-xs leading-4">
          {error}
        </p>
      ) : null}
    </div>,
    document.body,
  )
}

function PresetChip({
  label,
  onClick,
  disabled,
}: {
  label: string
  onClick: () => void
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="rounded-full py-1 px-2.5 bg-white border border-[#C9C2B6] text-[#1E1B17] text-[11px] leading-3 font-medium hover:bg-[#F7F5F0] cursor-pointer disabled:opacity-60"
    >
      {label}
    </button>
  )
}

// ──────────────────────────────────────────────────────
// Date helpers — operate on date-only ISO strings
// ("2026-05-12") in LOCAL time so the grid never shifts a
// day across a timezone boundary.

export function parseIso(iso: string | null): Date | null {
  if (!iso) return null
  const [y, m, d] = iso.split("-").map(Number)
  if (!y || !m || !d) return null
  return new Date(y, m - 1, d)
}

export function isoFromDate(date: Date): string {
  return isoFromYMD(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  )
}

export function isoFromYMD(y: number, m: number, d: number): string {
  const mm = String(m + 1).padStart(2, "0")
  const dd = String(d).padStart(2, "0")
  return `${y}-${mm}-${dd}`
}

export function addDays(date: Date, days: number): Date {
  const next = new Date(date)
  next.setDate(next.getDate() + days)
  return next
}

export function endOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0)
}
